/**
 * Rechte in der Oberflaeche (Verbesserungsplan, Paket 2 Schritt 1).
 *
 * Die Namen sind eine Kopie von RECHTE in
 * intermal/src/middleware/berechtigungen.ts. Die Oberflaeche entscheidet
 * nichts selbst: sie bekommt die aufgeloesten Rechte einer Person ueber
 * GET /api/auth/me (`rechte`) und blendet damit Gruppen, Seiten und Knoepfe
 * aus. Das Backend prueft jede Aktion unabhaengig davon (requireRecht).
 *
 * Bewusst ohne React, damit navigation/routes.ts die Funktion nutzen kann.
 * Wer die Liste im Backend aendert, aendert sie hier mit (ein Test im
 * Backend vergleicht beide Listen nicht, darum: Kommentar beachten).
 */
export const RECHTE = [
  'linkedin.lesen', 'linkedin.bearbeiten', 'linkedin.freigeben',
  'inhalte.lesen', 'inhalte.bearbeiten',
  'vertrieb.lesen', 'vertrieb.bearbeiten', 'vertrieb.loeschen',
  'angebot.lesen', 'angebot.bearbeiten', 'angebot.versenden',
  'versand.lesen', 'versand.ausfuehren',
  'erp.auftrag.lesen', 'erp.lager.lesen', 'erp.offene_posten.lesen',
  'erp.rechnung.lesen', 'erp.zahlung.lesen', 'erp.einkauf.lesen',
  'erp.bearbeiten', 'erp.ausstellen',
  'buchhaltung.lesen', 'buchhaltung.buchen',
  'buchhaltung.festschreiben', 'buchhaltung.datev', 'buchhaltung.konten',
  'projekt.lesen', 'zeit.eigene', 'projekt.verwalten', 'zeit.fremde',
  'erp.einstellungen.lesen', 'erp.einstellungen.bearbeiten',
  'admin.optionen', 'admin.benutzer', 'admin.mandant',
  'admin.mandant.alle',
] as const;
export type Recht = (typeof RECHTE)[number];

/** Alle ERP-Leserechte (Kopie von ERP_LESERECHTE im Backend): eines genuegt fuer den Bereitschafts-Check. */
export const ERP_LESERECHTE: readonly Recht[] = [
  'erp.auftrag.lesen', 'erp.lager.lesen', 'erp.offene_posten.lesen',
  'erp.rechnung.lesen', 'erp.zahlung.lesen', 'erp.einkauf.lesen',
];

export const APP_ROLLEN = ['mitarbeit', 'marketing', 'vertrieb', 'buchhaltung', 'inhaber'] as const;
export type AppRolle = (typeof APP_ROLLEN)[number];

/** Das, was von einer angemeldeten Person fuer Rechte-Fragen zaehlt. */
export interface RechteTraeger {
  rechte?: readonly string[] | null;
  appRolle?: string | null;
}

/**
 * Hat die Person das Recht? Ohne Benutzer oder ohne `rechte` (aelteres
 * Backend, Ladephase) immer false: lieber einen Knopf zu wenig zeigen als
 * einen 403 provozieren.
 */
export function hatRecht(user: RechteTraeger | null | undefined, recht: Recht): boolean {
  return Boolean(user?.rechte?.includes(recht));
}

/** Mindestens eines der Rechte vorhanden? */
export function hatEinesDerRechte(user: RechteTraeger | null | undefined, rechte: readonly Recht[]): boolean {
  return rechte.some((r) => hatRecht(user, r));
}

/** Alle gefragten Rechte vorhanden? */
export function hatAlleRechte(user: RechteTraeger | null | undefined, rechte: readonly Recht[]): boolean {
  return rechte.every((r) => hatRecht(user, r));
}

export function istAppRolle(wert: unknown): wert is AppRolle {
  return typeof wert === 'string' && (APP_ROLLEN as readonly string[]).includes(wert);
}

/** Anwendungsrolle der Person; unbekannt oder fehlend zaehlt als mitarbeit. */
export function appRolleVon(user: RechteTraeger | null | undefined): AppRolle {
  const rolle = user?.appRolle;
  return istAppRolle(rolle) ? rolle : 'mitarbeit';
}
